import axiosInstance from './axiosInstance';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/api';

// ===== Admin endpoints (use axiosInstance — baseURL includes /api) =====

export const getAssociativeRoles = async (params?: { categoria?: string; settore?: string }) => {
  const response = await axiosInstance.get('/admin/associative-roles', { params });
  return response.data;
};

export const getAssociativeRole = async (id: string) => {
  const response = await axiosInstance.get(`/admin/associative-roles/${id}`);
  return response.data;
};

export const createAssociativeRole = async (data: any) => {
  const response = await axiosInstance.post('/admin/associative-roles', data);
  return response.data;
};

export const updateAssociativeRole = async (id: string, data: any) => {
  const response = await axiosInstance.put(`/admin/associative-roles/${id}`, data);
  return response.data;
};

export const deleteAssociativeRole = async (id: string) => {
  const response = await axiosInstance.delete(`/admin/associative-roles/${id}`);
  return response.data;
};

export const getAssociativePermissions = async () => {
  const response = await axiosInstance.get('/admin/associative-roles/permissions/all');
  return response.data;
};

export const updateRolePermissions = async (roleId: string, permissionIds: string[]) => {
  const response = await axiosInstance.put(`/admin/associative-roles/${roleId}/permissions`, { permissionIds });
  return response.data;
};

// ===== Titolari di una carica (lista cariche) =====
export interface RoleMemberRep {
  _id: string;
  nome: string;
  cognome: string;
  emailPersonale?: string | null;
  foto?: string | null;
  regioneCompetenza?: string | null;
  provinciaCompetenza?: string | null;
  regioniCompetenza?: string[];
  provinceCompetenza?: string[];
}
export interface RoleHolder extends RoleMemberRep {
  status: string;
  dataInizio: string | null;
  caricaIndex: number;
}
export interface RoleHoldersState {
  titolari: RoleHolder[];
  activeRepresentatives: number;
}
export interface RoleMembersData extends RoleHoldersState {
  carica: { _id: string; nome: string; categoria: string; settore: string | null };
  territorio: { regione: boolean; provincia: boolean };
  candidati: RoleMemberRep[];
}

export const getAssociativeRoleRepresentatives = async (roleId: string): Promise<{ success: boolean; data: RoleMembersData }> => {
  const response = await axiosInstance.get(`/admin/associative-roles/${roleId}/representatives`);
  return response.data;
};

export const addAssociativeRoleRepresentative = async (
  roleId: string,
  data: {
    representativeId: string;
    regioniCompetenza?: string[];
    provinceCompetenza?: string[];
    regioneCompetenza?: string;
    provinciaCompetenza?: string;
  }
): Promise<{ success: boolean; data: RoleHoldersState; caricaNome: string }> => {
  const response = await axiosInstance.post(`/admin/associative-roles/${roleId}/representatives`, data);
  return response.data;
};

export const removeAssociativeRoleRepresentative = async (roleId: string, repId: string): Promise<{ success: boolean; data: RoleHoldersState; caricaNome: string }> => {
  const response = await axiosInstance.delete(`/admin/associative-roles/${roleId}/representatives/${repId}`);
  return response.data;
};

// ===== Policy cariche (ADR 0025) =====
export const getAssociativeRolePolicies = async () => {
  const response = await axiosInstance.get('/admin/associative-role-policies');
  return response.data;
};

export const getAssociativeRolePolicyOptions = async () => {
  const response = await axiosInstance.get('/admin/associative-role-policies/options');
  return response.data;
};

export const getAssociativeRolePolicy = async (roleId: string) => {
  const response = await axiosInstance.get(`/admin/associative-role-policies/${roleId}`);
  return response.data;
};

export const saveAssociativeRolePolicy = async (roleId: string, data: any) => {
  const response = await axiosInstance.put(`/admin/associative-role-policies/${roleId}`, data);
  return response.data;
};

export const setAssociativeRolePolicyCanRestrict = async (roleId: string, enabled: boolean) => {
  const response = await axiosInstance.put(`/admin/associative-role-policies/${roleId}/can-restrict`, { enabled });
  return response.data;
};

export const getRepresentatives = async (params?: any) => {
  const response = await axiosInstance.get('/admin/representatives', { params });
  return response.data;
};

export const getRepresentative = async (id: string) => {
  const response = await axiosInstance.get(`/admin/representatives/${id}`);
  return response.data;
};

export const createRepresentative = async (data: any) => {
  const response = await axiosInstance.post('/admin/representatives', data);
  return response.data;
};

export const updateRepresentative = async (id: string, data: any) => {
  const response = await axiosInstance.put(`/admin/representatives/${id}`, data);
  return response.data;
};

export const deleteRepresentative = async (id: string) => {
  const response = await axiosInstance.delete(`/admin/representatives/${id}`);
  return response.data;
};

export const assignCharge = async (repId: string, data: { caricaId: string; dataInizio: string }) => {
  const response = await axiosInstance.post(`/admin/representatives/${repId}/cariche`, data);
  return response.data;
};

export const endCharge = async (repId: string, caricaIdx: number, dataFine: string) => {
  const response = await axiosInstance.put(`/admin/representatives/${repId}/cariche/${caricaIdx}`, { dataFine });
  return response.data;
};

export const sendRepNotification = async (repId: string, type: 'welcome' | 'charge_assigned' | 'charge_removed', caricaNome?: string) => {
  const response = await axiosInstance.post(`/admin/representatives/${repId}/notify`, { type, caricaNome });
  return response.data;
};

export const resetPin = async (repId: string, sendNotification: boolean) => {
  const response = await axiosInstance.post(`/admin/representatives/${repId}/reset-pin`, { sendNotification });
  return response.data;
};

export const searchAssociates = async (q: string) => {
  const response = await axiosInstance.get('/admin/representatives/search/associates', { params: { q } });
  return response.data;
};

export const markRepresentativeAsReviewed = async (id: string) => {
  const response = await axiosInstance.post(`/admin/representatives/${id}/mark-reviewed`);
  return response.data;
};

// Esegue l'audit divergenze Representative↔Associate e aggiorna i flag needsReview.
export const runRepresentativeAudit = async () => {
  const response = await axiosInstance.post('/admin/representatives/run-audit');
  return response.data;
};

// ===== Representative area endpoints (dedicated axios with representative token) =====

const getRepToken = () => localStorage.getItem('representative_token');

// Axios dedicato all'area rappresentante: inietta il representative_token.
// Esportato per il riuso da altri servizi dell'area associativa (es. supporto).
export const repAxios = axios.create({ baseURL: API_BASE });
repAxios.interceptors.request.use(config => {
  const token = getRepToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

export const getMyProfile = async () => {
  const response = await repAxios.get('/representative/me');
  return response.data;
};

// Rilascia una sessione associato per lo studio collegato (rappresentante interno).
// Ritorna { success, data: { token, associate } }.
export const enterStudio = async () => {
  const response = await repAxios.post('/representative/enter-studio');
  return response.data;
};

export const updateMyProfile = async (data: any) => {
  const response = await repAxios.put('/representative/me', data);
  return response.data;
};

export const uploadMyFoto = async (file: File) => {
  const formData = new FormData();
  formData.append('foto', file);
  const response = await repAxios.post('/representative/me/foto', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
  return response.data;
};

export const removeMyFoto = async () => {
  const response = await repAxios.delete('/representative/me/foto');
  return response.data;
};

export const checkRepresentative = async (associateId: string) => {
  const token = localStorage.getItem('associate_token');
  const headers: any = {};
  if (token) headers.Authorization = `Bearer ${token}`;
  const response = await axios.get(`${API_BASE}/representative/check/${associateId}`, { headers });
  return response.data;
};

export const verifyPin = async (representativeId: string, pin?: string) => {
  const token = localStorage.getItem('associate_token');
  const headers: any = {};
  if (token) headers.Authorization = `Bearer ${token}`;
  const response = await axios.post(`${API_BASE}/representative/verify-pin`, { representativeId, pin }, { headers });
  return response.data;
};

// ===== Associates list (representative area) =====

export const getRepAssociates = async (params?: Record<string, string>) => {
  const response = await repAxios.get('/representative/associates', { params });
  return response.data;
};

export const getRepAssociateFieldOptions = async () => {
  const response = await repAxios.get('/representative/associates/field-options');
  return response.data;
};

export const getRepAssociateDetail = async (id: string) => {
  const response = await repAxios.get(`/representative/associates/${id}`);
  return response.data;
};

export const getRepAssociatesExportColumns = async () => {
  const response = await repAxios.get('/representative/associates/export/columns');
  return response.data;
};

export const downloadRepAssociatesExport = async (params: Record<string, string>) => {
  const response = await repAxios.get('/representative/associates/export', {
    params,
    responseType: 'blob'
  });
  return response;
};

export const saveRepresentativeToken = (token: string) => localStorage.setItem('representative_token', token);
export const getRepresentativeToken = () => localStorage.getItem('representative_token');
export const removeRepresentativeToken = () => localStorage.removeItem('representative_token');

// Verifica presenza + validità (scadenza) del representative_token.
// Mirror della logica in RepresentativePrivateRoute. NON modifica otpAuthService.isAuthenticated().
export const isRepresentativeAuthenticated = (): boolean => {
  const token = getRepresentativeToken();
  if (!token) return false;
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    const now = Date.now() / 1000;
    if (payload.exp && payload.exp < now) {
      removeRepresentativeToken();
      return false;
    }
    return true;
  } catch {
    removeRepresentativeToken();
    return false;
  }
};

// ===== Calendar events (representative area) =====

export const getRepEventTypes = async () => {
  const response = await repAxios.get('/representative/events/event-types');
  return response.data;
};

export const getRepEvents = async (params?: Record<string, string>) => {
  const response = await repAxios.get('/representative/events', { params });
  return response.data;
};

export const getRepEvent = async (id: string) => {
  const response = await repAxios.get(`/representative/events/${id}`);
  return response.data;
};

// QR personale del rappresentante per l'evento (sola lettura sull'invito esistente)
export const getRepEventMyQrcode = async (id: string) => {
  const response = await repAxios.get(`/representative/events/${id}/my-qrcode`);
  return response.data;
};

export const getRepEventMyQrcodePdf = async (id: string) => {
  const response = await repAxios.get(`/representative/events/${id}/my-qrcode/pdf`, { responseType: 'blob' });
  return response.data;
};

export const createRepEvent = async (data: any) => {
  const response = await repAxios.post('/representative/events', data);
  return response.data;
};

export const updateRepEvent = async (id: string, data: any) => {
  const response = await repAxios.put(`/representative/events/${id}`, data);
  return response.data;
};

export const deleteRepEvent = async (id: string) => {
  const response = await repAxios.delete(`/representative/events/${id}`);
  return response.data;
};

export const updateRepEventStato = async (id: string, stato: string) => {
  const response = await repAxios.put(`/representative/events/${id}/stato`, { stato });
  return response.data;
};

export const getRepEventInvitations = async (id: string, params?: Record<string, string>) => {
  const response = await repAxios.get(`/representative/events/${id}/invitations`, { params });
  return response.data;
};

export const getRepEventCaricheOptions = async () => {
  const response = await repAxios.get('/representative/events/cariche-options');
  return response.data;
};

export const getRepEventAudiencePreview = async (
  targetAudience: any,
  settori?: string[],
  authorCaricaId?: string,
  tipologiaId?: string
) => {
  const response = await repAxios.post('/representative/events/audience-preview', { targetAudience, settori, authorCaricaId, tipologiaId });
  return response.data;
};

export const getRepEventAssociates = async (id: string, params?: Record<string, string>) => {
  const response = await repAxios.get(`/representative/events/${id}/associates`, { params });
  return response.data;
};

export const getRepEventAssociateProvince = async (id: string) => {
  const response = await repAxios.get(`/representative/events/${id}/associate-province`);
  return response.data;
};

export interface InviteSelection {
  associateIds?: string[];
  tuttiAssociati?: boolean;
  cariche?: string[];
  // restringe il ramo "tutti gli associati del perimetro" per stato tesseramento
  statoFilter?: 'inregola' | 'nonregola';
  // associati esclusi a mano dal ramo "tutti gli associati del perimetro"
  excludeAssociateIds?: string[];
  // restringe il ramo "tutti" a una provincia (regionali/nazionali)
  provincia?: string;
  // chiede all'anteprima l'elenco nominativo dei destinatari (bottone "Mostra")
  includeList?: boolean;
}

export const generateRepEventInvitations = async (id: string, selection: InviteSelection) => {
  const response = await repAxios.post(`/representative/events/${id}/generate-invitations`, selection);
  return response.data;
};

export const getRepEventInvitationsPreview = async (id: string, selection: InviteSelection) => {
  const response = await repAxios.post(`/representative/events/${id}/invitations-preview`, selection);
  return response.data;
};

export const uploadRepEventAllegato = async (id: string, file: File) => {
  const formData = new FormData();
  formData.append('file', file);
  const response = await repAxios.post(`/representative/events/${id}/allegati`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
};

export const deleteRepEventAllegato = async (id: string, allegatoId: string) => {
  const response = await repAxios.delete(`/representative/events/${id}/allegati/${allegatoId}`);
  return response.data;
};

export const sendRepEventInvitations = async (id: string) => {
  const response = await repAxios.post(`/representative/events/${id}/send-invitations`);
  return response.data;
};

export const sendRepEventReminder = async (id: string) => {
  const response = await repAxios.post(`/representative/events/${id}/send-reminder`);
  return response.data;
};

export const toggleRepEventCheckin = async (eventId: string, invitationId: string) => {
  const response = await repAxios.put(`/representative/events/${eventId}/invitations/${invitationId}/toggle-checkin`);
  return response.data;
};

// ===== Regia: azione primaria adattiva =====

export type EventLifecycleAction =
  | { kind: 'publish-and-send'; pendingInvites: number }   // bozza + invitati pronti
  | { kind: 'publish-only' }                               // bozza senza invitati
  | { kind: 'send-new'; pendingInvites: number }           // pubblicato + nuovi inviti da spedire
  | { kind: 'none' };                                       // niente azione primaria

/**
 * Determina l'azione primaria della regia in base allo stato evento e agli inviti.
 * @param stato stato evento (BOZZA/PUBBLICATO/...)
 * @param hasSelection true se ci sono invitati selezionati ma non ancora generati
 * @param pendingInvites n. EventInvitation con email non ancora inviata
 */
export const getEventLifecycleAction = (
  stato: string,
  hasSelection: boolean,
  pendingInvites: number,
): EventLifecycleAction => {
  if (stato === 'BOZZA') {
    return (hasSelection || pendingInvites > 0)
      ? { kind: 'publish-and-send', pendingInvites }
      : { kind: 'publish-only' };
  }
  // Dopo la pubblicazione: inviti generati ma non spediti OPPURE nuova
  // selezione ancora da generare — in entrambi i casi un solo passaggio
  // "genera (se serve) + invia"
  if ((stato === 'PUBBLICATO' || stato === 'IN_CORSO') && (pendingInvites > 0 || hasSelection)) {
    return { kind: 'send-new', pendingInvites };
  }
  return { kind: 'none' };
};

// ===== Verbale =====

export const getRepEventVerbale = async (eventId: string) => {
  const response = await repAxios.get(`/representative/events/${eventId}/verbale`);
  return response.data;
};

export const saveRepEventVerbale = async (eventId: string, contenuto: string) => {
  const response = await repAxios.put(`/representative/events/${eventId}/verbale`, { contenuto });
  return response.data;
};

export const uploadVerbaleAllegato = async (eventId: string, file: File) => {
  const formData = new FormData();
  formData.append('file', file);
  const response = await repAxios.post(`/representative/events/${eventId}/verbale/allegati`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
  return response.data;
};

export const deleteVerbaleAllegato = async (eventId: string, allegatoId: string) => {
  const response = await repAxios.delete(`/representative/events/${eventId}/verbale/allegati/${allegatoId}`);
  return response.data;
};

export const publishRepEventVerbale = async (eventId: string) => {
  const response = await repAxios.post(`/representative/events/${eventId}/verbale/publish`);
  return response.data;
};

export const unpublishRepEventVerbale = async (eventId: string) => {
  const response = await repAxios.post(`/representative/events/${eventId}/verbale/unpublish`);
  return response.data;
};

export const getVerbaleChat = async (eventId: string) => {
  const response = await repAxios.get(`/representative/events/${eventId}/verbale/chat`);
  return response.data;
};

export const sendVerbaleChatMessage = async (eventId: string, message: string) => {
  const response = await repAxios.post(`/representative/events/${eventId}/verbale/chat`, { message });
  return response.data;
};

// ===== Comunicazioni tra rappresentanti (representative area) =====

export interface RepCommParticipant {
  _id: string;
  nome: string;
  cognome: string;
  foto: string | null;
  caricaPrincipale: string | null;
}

export type RepCommRelation = 'peer' | 'superior' | 'subordinate';

export interface RepCommContact extends RepCommParticipant {
  relation: RepCommRelation;
}

export interface RepCommConversation {
  _id: string;
  type: 'direct' | 'room';
  name: string | null;
  participants: RepCommParticipant[];
  lastMessage: { content: string; sender: string; timestamp: string } | null;
  unread: number;
  updatedAt: string;
}

// Il sender dei messaggi è popolato dal backend solo con nome/cognome/foto
export interface RepCommSender {
  _id: string;
  nome: string;
  cognome: string;
  foto?: string | null;
}

export interface RepCommMessage {
  _id: string;
  conversation: string;
  sender: RepCommSender;
  content: string;
  createdAt: string;
}

export const getCommContacts = async () => {
  const response = await repAxios.get('/representative/communications/contacts');
  return response.data;
};

export const getCommConversations = async () => {
  const response = await repAxios.get('/representative/communications/conversations');
  return response.data;
};

export const openCommConversation = async (recipientId: string) => {
  const response = await repAxios.post('/representative/communications/conversations', { recipientId });
  return response.data;
};

export const getCommMessages = async (conversationId: string, params?: Record<string, string>) => {
  const response = await repAxios.get(`/representative/communications/conversations/${conversationId}/messages`, { params });
  return response.data;
};

export const sendCommMessage = async (conversationId: string, content: string) => {
  const response = await repAxios.post(`/representative/communications/conversations/${conversationId}/messages`, { content });
  return response.data;
};

export const markCommRead = async (conversationId: string) => {
  const response = await repAxios.post(`/representative/communications/conversations/${conversationId}/read`);
  return response.data;
};

export const getCommUnreadCount = async () => {
  const response = await repAxios.get('/representative/communications/unread-count');
  return response.data;
};

// ===== Stanze comunicazione (admin — axiosInstance) =====

export interface RepCommRoom {
  _id: string;
  name: string;
  isActive: boolean;
  createdAt: string;
  createdBy: string | null;
  lastMessageAt: string | null;
  messagesCount: number;
  participants: Array<RepCommParticipant & { status: string }>;
}

export const getCommRooms = async () => {
  const response = await axiosInstance.get('/admin/representative-communications/rooms');
  return response.data;
};

export const createCommRoom = async (data: { name: string; participantIds: string[] }) => {
  const response = await axiosInstance.post('/admin/representative-communications/rooms', data);
  return response.data;
};

export const updateCommRoom = async (id: string, data: { name?: string; participantIds?: string[]; isActive?: boolean }) => {
  const response = await axiosInstance.put(`/admin/representative-communications/rooms/${id}`, data);
  return response.data;
};

export const deleteCommRoom = async (id: string) => {
  const response = await axiosInstance.delete(`/admin/representative-communications/rooms/${id}`);
  return response.data;
};

export interface CommRepsPickerParams {
  search?: string;
  caricaIds?: string[]; // selezione per carica (attiva)
  regione?: string;
  provincia?: string;
}

export const getCommRepsPicker = async (params?: CommRepsPickerParams) => {
  const query: Record<string, string> = {};
  if (params?.search) query.search = params.search;
  if (params?.caricaIds?.length) query.caricaIds = params.caricaIds.join(',');
  if (params?.regione) query.regione = params.regione;
  if (params?.provincia) query.provincia = params.provincia;
  const response = await axiosInstance.get('/admin/representative-communications/representatives-picker', {
    params: Object.keys(query).length ? query : undefined
  });
  return response.data;
};

export interface CommCaricaOption {
  _id: string;
  nome: string;
  categoria: string;
  settore: string | null;
  livello: number;
}

export const getCommCaricheOptions = async () => {
  const response = await axiosInstance.get('/admin/representative-communications/cariche-options');
  return response.data;
};

export const getCommTerritoryOptions = async () => {
  const response = await axiosInstance.get('/admin/representative-communications/territory-options');
  return response.data;
};
